import { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Info } from "lucide-react";

interface PreviousAnswerPopoverProps {
  label: string;
  value: string;
  details?: string | undefined;
  periodOrVersion: string;
}

export default function PreviousAnswerPopover({
  label,
  value,
  details,
  periodOrVersion,
}: PreviousAnswerPopoverProps) {
  const [show, setShow] = useState(false);
  const iconRef = useRef<HTMLSpanElement>(null);

  const getPopoverStyle = (): React.CSSProperties => {
    if (!iconRef.current) return { display: "none" };
    const rect = iconRef.current.getBoundingClientRect();
    return {
      position: "fixed",
      left: rect.left + rect.width / 2,
      top: rect.bottom + 8,
      transform: "translateX(-50%)",
      zIndex: 9999,
    };
  };

  return (
    <span
      ref={iconRef}
      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 border border-blue-200 dark:border-blue-800 transition-colors cursor-pointer"
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      <Info className="h-3.5 w-3.5" />
      <span className="hidden sm:inline">Previous Answer</span>
      {show &&
        createPortal(
          <div style={getPopoverStyle()} className="pointer-events-none">
            <div className="flex justify-center">
              <div className="w-0 h-0 border-l-[7px] border-l-transparent border-r-[7px] border-r-transparent border-b-[7px] border-b-white dark:border-b-gray-900" />
            </div>
            <div className="w-72 rounded-xl bg-white dark:bg-gray-900 shadow-[0_20px_60px_-12px_rgba(0,0,0,0.25)] dark:shadow-[0_20px_60px_-12px_rgba(0,0,0,0.6)] ring-1 ring-black/[0.06] dark:ring-white/[0.08] overflow-hidden">
              <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800 border-b border-gray-100 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Previous Assessment
                  </span>
                  <span className="text-xs font-semibold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 px-2 py-1 rounded-md">
                    {periodOrVersion}
                  </span>
                </div>
              </div>
              <div className="p-4">
                <div className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1">
                  {label}
                </div>
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {value}
                </p>
                {details && (
                  <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-800">
                    <div className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1">
                      Details
                    </div>
                    <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed line-clamp-3">
                      {details}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>,
          document.body,
        )}
    </span>
  );
}
